import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { ApiEndpoints } from '@/config/index';
import { LoggerService } from '@/utils';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { AuthStore } from '@/store/store.auth';
import { DexieQuickEditModel } from '@/models/dexie/DexieQuickEditModel';
import { QuickEditModel } from '@/models/quickEdit/QuickEdit';
import { Helpers } from '@/helpers/Helpers';
import { QuickEditBaseModel } from '@/models/quickEdit/QuickEditBase';
import { AxiosService, OnlineService } from '@/services';
import { LiteEvent } from '@/models/event/liteEvent';
import { ConfigurationSection } from '@/models/configuration/Section';
import { ConfigurationService } from './index';
import { QuickEditSectionModel } from '@/models/quickEdit/QuickEditSection';
import { QuickEditFieldModel } from '@/models/quickEdit/QuickEditField';
import { ConfigurationField } from '@/models/configuration/Field';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { ApiIdentifers } from '../config/index';

class QuickEditProvider {

    /**
     * Configuration for quick edits
     */
    public quickEditConfigurations: QuickEditModel[] = [];

    public quickEditConfigurationsFromWizard?: QuickEditModel;

    /**
     * Items to show in create menu
     */
    public quickEditItems: QuickEditBaseModel[] = [];

    /**
     * Used to store actual data-set from quick edit edit
     */
    public usedQuickEditConfigurationSections: ConfigurationSection[] = [];

    /**
     * Used to store actual samples configuration sections setup for quick edit edit
     */
    public usedQuickEditSamplesConfigurationSection?: ConfigurationSection;

    private readonly onQuickEditItemsChanged = new LiteEvent<QuickEditBaseModel[]>();

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;

    // tslint:disable-next-line: no-empty
    constructor() {}


    /**
     * Fetch whole quick edit configurations file
     */
    public async FetchQuickEdit() {
        this.quickEditConfigurations = await this.SyncQuickEdits();
        this.FetchQuickEditItems();
    }

    /**
     * Method to set usedQuickEditConfigurationSections
     * @param inSections Array of ConfigurationSection's
     */
    public SetUsedQuickEditConfigurationSections(inSections: ConfigurationSection[]) {
        this.usedQuickEditConfigurationSections = inSections;
    }

    /**
     * Clear the used sections
     */
    public ClearUsedQuickEditConfigurationSections() {
        this.usedQuickEditConfigurationSections.splice(0, this.usedQuickEditConfigurationSections.length);
    }

    /**
     * Add or update a section
     * @param inSection The ConfigurationSection object
     */
    public SetUsedQuickEditConfigurationSection(inSection: ConfigurationSection) {
        let section = this.usedQuickEditConfigurationSections.find((sec) => sec.identifier === inSection.identifier);
        if (section === undefined) {
            this.usedQuickEditConfigurationSections.push(inSection);
        } else {
            section = inSection;
        }
    }

    /**
     * Remove a section
     * @param inSection The ConfigurationSection object
     */
    public RemoveUsedQuickEditConfigurationSection(inSection: ConfigurationSection) {
        const sectionIndex = this.usedQuickEditConfigurationSections.map((sec) => sec.identifier)
            .indexOf(inSection.identifier);
        if (sectionIndex > -1) {
            this.usedQuickEditConfigurationSections.splice(sectionIndex, 1);
        }
    }

    /**
     * Method to generate on array  of all used quick edit configuration sections
     */
    public GenerateUsedQuickEditConfigurationSections(usedSections: QuickEditSectionModel[]) {
        const configurationSections: ConfigurationSection[] = [];
        const machinesConfigurationSections: ConfigurationSection[] = [];
        const samplesCconfigurationSections: ConfigurationSection[] = [];

        // add filtered general sections
        ConfigurationService.GetConfiguration.sections.forEach((section) => {
            const secQV = usedSections.find((x) => x.identifier === section.identifier);
            if (secQV != null) {
                const tmpSec = new ConfigurationSection(section);
                const fields = section.fields.filter(this.filterUsedFields(secQV.fields));
                tmpSec.fields = [];
                fields.forEach((field) => {
                    tmpSec.fields.push(new ConfigurationField(field));
                });

                /*
                * In case we have an action, we need to put the fields inside a new field
                */
                if (tmpSec.action !== undefined) {
                    ConfigurationService.generateActionSection(tmpSec);
                }

                configurationSections.push(tmpSec);
            }
        });

        // add filtered machine sections
        ConfigurationService.GetMachinesConfiguration.sections.forEach((section) => {
            const secQV = usedSections.find((x) => x.identifier === section.identifier);
            if (secQV != null) {
                const tmpSec = new ConfigurationSection(section);
                const fields = section.fields.filter(this.filterUsedFields(secQV.fields));
                tmpSec.fields = [];
                fields.forEach((field) => {
                    tmpSec.fields.push(new ConfigurationField(field));
                });
                tmpSec.position += ApiIdentifers.machinesPositionMultiplier;
                machinesConfigurationSections.push(tmpSec);
            }
        });

        // add sample section, if defined
        const sampleSecQV = usedSections.find((x) => x.identifier === ApiIdentifers.sample);
        if (sampleSecQV != null) {
            const sampleConfigSection = ConfigurationService.GetSamplesConfiguration.sections
                .find((sec) => sec.identifier === ApiIdentifers.sample);
            if (sampleConfigSection != null) {
                const tmpSec = new ConfigurationSection(sampleConfigSection);
                const fields = tmpSec.fields.filter(this.filterUsedFields(sampleSecQV.fields));
                tmpSec.fields = [];
                fields.forEach((field) => {
                    tmpSec.fields.push(new ConfigurationField(field));
                });
                tmpSec.position += ApiIdentifers.samplesPositionMultiplier;
                ConfigurationService.generateActionSection(tmpSec);
                samplesCconfigurationSections.push(tmpSec);
            }
        }

        this.usedQuickEditSamplesConfigurationSection = samplesCconfigurationSections[0] || undefined;
        this.usedQuickEditConfigurationSections =
            [...configurationSections, ...machinesConfigurationSections, ...samplesCconfigurationSections];

    }

    /**
     * Getter for used configuration
     */
    get GetQuickEditConfigurations() {
        return this.quickEditConfigurations;
    }

    /**
     * Getter for used items
     */
    get GetQuickEditItems() {
        return this.quickEditItems;
    }

    /**
     * Getter for used quick edit configuration sections
     */
    get GetUsedQuickEditConfigurationSections() {
        return this.usedQuickEditConfigurationSections;
    }

    /**
     * Getter for used quick edit samples configuration section
     */
    get GetUsedQuickEditSamplesConfigurationSection() {
        return this.usedQuickEditSamplesConfigurationSection;
    }

    /**
     * Event getter for quick select menu items on dashboard
     */
    public get OnQuickEditItemsChanged() {
        return this.onQuickEditItemsChanged.expose();
    }

    /**
     * Since we should access quick Edits only via this.quickEditConfigurations
     * all Methods accessing dexie db should be private here
     */

    /**
     * Get all quick Edit configurations
     */
    private async GetAllQuickEdits(): Promise<DexieQuickEditModel[]> {
        return this.dexieDB.getTable<DexieQuickEditModel>(this.dexieDB!.getTables().quickEditConfigurations).toArray();
    }

    /**
     * Add a new quick Edit configuration
     * @param quickEdit DexieQuickEditModel
     */
    private async AddQuickEdit(quickEdit: DexieQuickEditModel): Promise<number> {
        return this.dexieDB.add<DexieQuickEditModel>(this.dexieDB!.getTables().quickEditConfigurations, quickEdit);
    }

    /**
     * Put/Update an quick edit configuration
     * @param quickEdit DexieQuickEditModel
     */
    private async PutQuickEdit(quickEdit: DexieQuickEditModel): Promise<number> {
        return this.dexieDB.put<DexieQuickEditModel>(this.dexieDB!.getTables().quickEditConfigurations, quickEdit);
    }

    /**
     * Put/Update a set of quick edit configurations
     * @param quickEdits A list of DexieQuickEditModels
     */
    private async PutQuickEdits(quickEdits: DexieQuickEditModel[]): Promise<number> {
        return this.dexieDB.bulkPut<DexieQuickEditModel>(this.dexieDB!.getTables().quickEditConfigurations, quickEdits);
    }

    /**
     * Delete an quick edit configuration
     * @param quickEdit DexieQuickEditModel
     */
    private async DeleteQuickEdit(quickEdit: DexieQuickEditModel): Promise<void> {
        return this.dexieDB.del<DexieQuickEditModel>(this.dexieDB!.getTables().quickEditConfigurations, quickEdit.id);
    }

    private async DeleteAllQuickEdits(): Promise<void> {
        return this.dexieDB.clear<DexieDataSetEvent>(this.dexieDB!.getTables().quickEditConfigurations);
    }

    /**
     * Create items for the quick select menu on dashboard
     */
    private FetchQuickEditItems() {
        this.quickEditItems = [];
        this.quickEditConfigurations.forEach((qvc) => {
            this.quickEditItems.push(new QuickEditBaseModel(qvc));
        });
        this.onQuickEditItemsChanged.trigger(this.quickEditItems);
    }

    private async SyncQuickEdits(): Promise<QuickEditModel[]> {
        const tableStrings = this.dexieDB.getTables();
        let onlineQuickEdits: QuickEditModel[];
        let dexieQuickEdits: DexieQuickEditModel[];

        dexieQuickEdits = await this.GetAllQuickEdits();

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            await AxiosService.get(ApiEndpoints.quickEdit, null)
                .then((response) => {
                    onlineQuickEdits = response.data as QuickEditModel[];
                });
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`,
                OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        const result: Promise<QuickEditModel[]> = new Promise<QuickEditModel[]>(async (resolve, reject) => {
            // check if received an online and offline config
            if (onlineQuickEdits !== undefined && dexieQuickEdits !== undefined) {
                LoggerService.LogDebug(`checking for new updates`);
                resolve(this.checkForUpdates(onlineQuickEdits, dexieQuickEdits));
            } else if (onlineQuickEdits === undefined && dexieQuickEdits !== undefined) {
                // check if we only got an offline config
                LoggerService.LogDebug(`Using dexie quick edits since no online data found`);
                resolve(dexieQuickEdits as QuickEditModel[]);
            } else if (onlineQuickEdits !== undefined && dexieQuickEdits === undefined) {
                // check if we only got an online config
                LoggerService.LogDebug(`Using online quick edits since no dexie data found`);
                for (const onlineQuickEdit of onlineQuickEdits) {
                    // save to as new entrie to dexiedb
                    await this.PutQuickEdit(onlineQuickEdit)
                        .catch((error) => {
                            LoggerService.LogError(`Error on adding new quick edit: `, error);
                        });
                }
                resolve(onlineQuickEdits);
            } else {
                LoggerService.LogDebug(`Both configuration sources are empty. Are you offline and never synced data before?`);
                reject(`Both configuration sources are empty. Are you offline and never synced data before?`);
            }
        });
        return result;
    }

    private async checkForUpdates(
        onlineQuickEdits: QuickEditModel[],
        dexieQuickEdits: DexieQuickEditModel[],
    ): Promise<QuickEditModel[]> {
        const newQEs: QuickEditModel[] = [];
        const updatedDQEs: DexieQuickEditModel[] = [];

        onlineQuickEdits.forEach((onlineQuickEdit) => {
            const dexieQuickEdit = dexieQuickEdits.find((dqe) => dqe.formId === onlineQuickEdit.formId);
            // if we found an existing dexieQuickEdit, check if the online one is newer
            if (dexieQuickEdit != null
                && onlineQuickEdit.date != null
                && dexieQuickEdit.date != null) {
                if (Helpers.compareDateStrings(onlineQuickEdit.date, dexieQuickEdit.date) > 0) {
                    /**
                     * online entry is newer than existing
                     * temporary save updated one
                     */
                    const updatedDQV = new DexieQuickEditModel(onlineQuickEdit);
                    updatedDQV.id = dexieQuickEdit.id;
                    updatedDQEs.push(updatedDQV);
                }
            } else {
                // must be a new entry
                newQEs.push(onlineQuickEdit);
            }
        });


        // update entries
        for (const updatedDQE of updatedDQEs) {
            // put updated entrie to dexiedb
            await this.PutQuickEdit(updatedDQE)
                .catch((error) => {
                    LoggerService.LogError(`Error on adding updated quick edit: `, error);
                });
        }

        for (const newQE of newQEs) {
            // put new entrie to dexiedb
            await this.PutQuickEdit(newQE)
                .catch((error) => {
                    LoggerService.LogError(`Error on adding new quick edit: `, error);
                });
        }

        // load updated entries from dexie db, so we will have same order of entries
        return await this.GetAllQuickEdits();
    }

    private filterUsedFields(usedFields: QuickEditFieldModel[]): any {
        return (inField: ConfigurationField) => {
            const usedField = usedFields.find((uf) => uf.identifier === inField.identifier);
            if (usedField != null) {
                if (inField.fields) {
                    return (inField.fields = inField.fields.map((o) => Object.assign({}, o))
                        .filter(this.filterUsedFields(usedField.fields || []))).length;
                } else {
                    return true;
                }
            } else {
                return false;
            }
        };
    }

}

export default QuickEditProvider;
